import { fromUTCToTimeZone, dateFNS } from '@gogame/intl-ts';

import {
  SCALE_HOURLY,
  SCALE_DAILY,
  SCALE_MONTHLY,
  SCALE_WEEKLY,
  SCALE_QUARTERLY,
  SCALE_MONTH_TO_DATE,
  START_DAY_MON,
  START_DAY_TUE,
  START_DAY_WED,
  START_DAY_THU,
  START_DAY_FRI,
  START_DAY_SAT,
  START_DAY_SUN,
} from './ComposedChart/constants';

export const COLORS = [
  '#4f77a0',
  '#F06292',
  '#FF7518',
  '#8A2BE2',
  '#648C11',
  '#DC143C',
  '#FFB74D',
  '#81C784',
  '#BA68C8',
  '#058DC7',
  '#7F77EA',
  '#0EB05C',
  '#902C2F',
  '#F78072',
  '#C7B590',
  '#FFCC33',
  '#16D9EB',
  '#F05D0E',
  '#D9BB41',
  '#3399FF',
];

export const getColorByIndex = (index) => COLORS[index % COLORS.length];

export const initDefaultColorGetter = () => {
  const uniqColorKeys = [];

  return (colorKey) => {
    if (!uniqColorKeys.includes(colorKey)) {
      return getColorByIndex(uniqColorKeys.push(colorKey) - 1);
    }
    return getColorByIndex(uniqColorKeys.indexOf(colorKey));
  };
};

const getLocalDate = (date) => fromUTCToTimeZone(date, 'utc');

const startOfLocalWeek = (scaleWeekStart) => (d) => {
  const weekStartsOn = {
    [START_DAY_MON]: 1,
    [START_DAY_TUE]: 2,
    [START_DAY_WED]: 3,
    [START_DAY_THU]: 4,
    [START_DAY_FRI]: 5,
    [START_DAY_SAT]: 6,
    [START_DAY_SUN]: 0,
  }[scaleWeekStart];

  return dateFNS.startOfWeek(d, { weekStartsOn });
};

export const getPeriodDateIndex =
  (start, offset = 0, scale = SCALE_DAILY, scaleWeekStart = START_DAY_MON) =>
  (date) => {
    const diff = {
      [SCALE_HOURLY]: dateFNS.differenceInHours,
      [SCALE_DAILY]: dateFNS.differenceInDays,
      [SCALE_WEEKLY]: dateFNS.differenceInWeeks,
      [SCALE_MONTHLY]: dateFNS.differenceInMonths,
      [SCALE_MONTH_TO_DATE]: dateFNS.differenceInMonths,
      [SCALE_QUARTERLY]: dateFNS.differenceInCalendarQuarters,
    }[scale];

    const startOf = {
      [SCALE_HOURLY]: dateFNS.startOfHour,
      [SCALE_DAILY]: dateFNS.startOfDay,
      [SCALE_WEEKLY]: startOfLocalWeek(scaleWeekStart),
      [SCALE_MONTHLY]: dateFNS.startOfMonth,
      [SCALE_MONTH_TO_DATE]: dateFNS.startOfMonth,
      [SCALE_QUARTERLY]: dateFNS.startOfQuarter,
    }[scale];

    const dateDiff = diff(
      startOf(getLocalDate(date)),
      startOf(getLocalDate(start))
    );

    return start ? dateDiff - offset : 0;
  };

export const getLineModeDasharray = ({ mode, dashed }) => {
  let dashA = '';
  let dashB = '';

  if (dashed) {
    dashB = '4';
  }

  if (mode) {
    switch (mode) {
      case 'SUM':
      case 'CHR':
      case 'CHD':
        dashA = '3';
        break;
    }
  }

  if (!dashA && !dashB) {
    return null;
  }
  return `${dashA} ${dashB}`;
};

export const getDateByPeriodIndex =
  (start, offset, scale, scaleWeekStart = START_DAY_MON) =>
  (index) => {
    const add = {
      [SCALE_HOURLY]: dateFNS.addHours,
      [SCALE_DAILY]: dateFNS.addDays,
      [SCALE_WEEKLY]: dateFNS.addWeeks,
      [SCALE_MONTHLY]: dateFNS.addMonths,
      [SCALE_MONTH_TO_DATE]: dateFNS.addMonths,
      [SCALE_QUARTERLY]: dateFNS.addQuarters,
    }[scale];

    const startOf = {
      [SCALE_HOURLY]: dateFNS.startOfHour,
      [SCALE_DAILY]: dateFNS.startOfDay,
      [SCALE_WEEKLY]: startOfLocalWeek(scaleWeekStart),
      [SCALE_MONTHLY]: index ? dateFNS.startOfMonth : dateFNS.startOfDay,
      [SCALE_MONTH_TO_DATE]: index ? dateFNS.startOfMonth : dateFNS.startOfDay,
      [SCALE_QUARTERLY]: dateFNS.startOfQuarter,
    }[scale];

    return add(startOf(getLocalDate(start)), index + offset);
  };
